import AES from "crypto-js/aes"
import utf8 from "crypto-js/enc-utf8"
import parsePhone from "@/utils/PhoneUtils"
import AbstractVendor from "../AbstractVendor"
import message from "@/utils/message"

const ChannelSoftDialDirectionMap = {
  CallTypeOutbound: "DIAL_DIRECTION_OUT",
  CallTypeInbound: "DIAL_DIRECTION_IN",
}

class ChannelSoft extends AbstractVendor {
  constructor(agent, isCallTelWithExt) {
    super()
    this.name = "CALL_CENTER_VENDOR_CHANNELSOFT"
    this.agent = agent
    this.dialStatus = {}
    this.isCallTelWithExt = isCallTelWithExt
  }

  bind({ agencyId, userId, callCenter }) {
    this.agencyId = agencyId
    this.userId = userId
    this.callCenterInfo = callCenter.substr(callCenter.indexOf(":") + 1)
  }

  /**
   * 注册呼出回调
   */
  onDialOut(callback) {
    this.dialOutCallback = callback
  }

  login() {
    return new Promise((resolve, reject) => {
      if (!this.callCenterInfo) {
        reject("未设置呼叫中心信息")
        return
      }

      this.agent.init({
        visible: false,
        telVisible: true,
        isShowIntelSelect: false,
        ui: true,
        useLocal: false,
        position: {
          right: document.body.clientWidth - 100,
          bottom: 220,
        },
        sipUseCphone: false,
        // sipUseCphone: sipUseCphone,//sip分机时使用Cphone登录：true,不使用：false
        callback: () => {
          // 重写makeCall方法，在青牛面板上呼叫定义为manual；在前端的面板上呼出的定位为program
          {
            const vm = this.agent.vm
            const makeCall = vm.makeCall
            vm.makeCall = () => {
              const number = vm.callNumber()
              if (!number) {
                return
              }

              const el = document.querySelector("#WA-div")
              el.style.display = "none"

              let callbackParam = {
                mode: "manual", // 手动输入
                isIntellectCall: vm.outCallNoType && vm.outCallNoType() === "intelSelect",
                callerNumber: vm.disNumber(),
                calleeNumber: number,
                makeCall: makeCall,
              }

              // 回调 CallCenter，由其判断是否需要继续呼叫
              this.dialOutCallback(callbackParam)
            }
          }

          // 监听操作是否成功
          this.agent.registerEventHandler(this.dispatchEvent)
          this.agent.registerResultHandler(this.dispatchResult)

          const sleep = () => new Promise((r) => setTimeout(r, 1000))
          this.doLogin().then(resolve, () => {
            this.doLogout().then(sleep, sleep).then(this.doLogin, this.doLogin).then(resolve, reject)
          })
        },
      })
    })
  }

  doLogin = () => {
    return new Promise((resolve, reject) => {
      const callCenterInfo = JSON.parse(AES.decrypt(this.callCenterInfo.substr(8), this.callCenterInfo.substr(0, 8)).toString(utf8))
      let telNo
      switch (callCenterInfo["p"]) {
        case "m":
          telNo = "tel:" + this.parseMobile(callCenterInfo["m"].trim())
          break
        case "t":
          telNo = "tel:" + this.parseTelephone(callCenterInfo["t"].trim())
          break
        case "s":
        case "d":
          telNo = "sip:" + callCenterInfo["s"].trim()
          break
        default:
          throw new Error("未知的分机号码")
      }
      if (!telNo) {
        throw new Error("非法的分机号码")
      }

      this.loginPromise = { resolve, reject }
      this.agent.extend.login({
        entId: callCenterInfo["cc"],
        agentId: callCenterInfo["st"],
        agentPassword: callCenterInfo["pw"],
        agentNumber: telNo,
        isForce: true,
      })
    })
  }

  doLogout() {
    return new Promise((resolve, reject) => {
      this.logoutPromise = { resolve, reject }
      this.agent.extend.logout()
    })
  }

  dialOut(number, params) {
    const vm = this.agent.vm

    // 初始化未完成，或者无外显号码，则不允许呼叫
    if (!vm || !vm.disNumber()) {
      return Promise.reject("初始化未完成，或者无外显号码")
    }

    const makeCall = (number, extraParams, extraResp) => {
      this.dialStatus.number = number
      this.dialStatus.params = Object.assign({}, params, extraParams)
      this.dialStatus.resp = extraResp || {}
      number = this.parseCustomerNumber(number)

      const status = vm.currentState().key
      return (status !== "BUSY" ? this.setBusy() : Promise.resolve())
        .then(() => {
          return new Promise((resolve, reject) => {
            this.disconnectPromise = { resolve, reject }

            let makeCallParam = {
              outCallNumber: number, // 被叫号码
              disNumber: vm.disNumber(), // 主叫（外显）号码
            }

            if (vm.outCallNoType) {
              switch (vm.outCallNoType()) {
                case "intelSelect":
                  makeCallParam = {
                    outCallNumber: number, // 被叫号码
                    isIntellectCall: true, // 智能外显
                  }
                  break
                case "manualSelect":
                default:
                  break
              }
            }

            this.agent.extend.makeCall(makeCallParam)
          })
        })
        .then(() => {
          // 恢复拨号前忙闲状态
          const sleep = () => new Promise((r) => setTimeout(r, 1000))
          return sleep().then(() => {
            const currentStatus = vm.currentState().key
            if (currentStatus !== status) {
              if (status === "BUSY") {
                return this.setBusy()
              } else if (status === "READY") {
                return this.setReady()
              }
            }
          })
        })
    }

    let precheckParam = {
      mode: "program", // 程序触发
      isIntellectCall: vm.outCallNoType && vm.outCallNoType() === "intelSelect",
      callerNumber: vm.disNumber(),
      calleeNumber: number,
      makeCall: makeCall,
      ...params,
    }

    // 回调 CallCenter，由其判断是否需要继续呼叫
    const precheck = this.dialOutCallback ? this.dialOutCallback(precheckParam) : Promise.resolve()

    return precheck
  }

  setBusy() {
    return new Promise((resolve, reject) => {
      this.busyPromise = { resolve, reject }
      this.agent.extend.setBusy()
    })
  }

  setReady() {
    return new Promise((resolve, reject) => {
      this.readyPromise = { resolve, reject }
      this.agent.extend.setReady()
    })
  }

  setAway() {
    return new Promise((resolve, reject) => {
      this.readyPromise = { resolve, reject }
      this.agent.extend.setAway()
    })
  }

  onUpdateStatus(callback) {
    this.onUpdateStatusCallback = callback
  }

  onDialStatusUpdate(callback) {
    this.onDailStatusUpdateCallback = callback
  }

  // 自动弹出拨号面板
  autoShowPanel() {
    const el = document.querySelector("#WA-div")
    el.style.display = "block"
  }

  dispatchEvent = (data) => {
    switch (data.type) {
      //登录成功事件
      case "EVENT_WEBAGENT_LOGIN":
        return this.onLogin(data)
      //登录失败事件
      case "EVENT_WEBAGENT_LOGIN_FAIL":
        return this.onLoginFail(data)
      //登出成功事件
      case "EVENT_WEBAGENT_LOGOUT":
        return this.onLogout(data)
      //登出失败事件
      case "EVENT_WEBAGENT_LOGOUT_FAIL":
        return this.onLogoutFail(data)
      //外呼坐席振铃事件
      case "EVENT_OUTBOUND_ALERTING_TP":
        return this.onOutboundAlertingTp(data)
      //外呼坐席接通事件
      case "EVENT_OUTBOUND_CONNECTED_OP":
        return this.onOutboundConnectedOp(data)
      //坐席挂断事件
      case "EVENT_TP_DISCONNECT":
        return this.onTpDisconnect(data)
      //客户挂断事件
      case "EVENT_OP_DISCONNECT":
        return this.onOpDisconnect(data)
      //置忙事件
      case "EVENT_AGENT_NOTREADY":
        return this.onBusy(data)
      //置闲事件
      case "EVENT_AGENT_READY":
        return this.onReady(data)
      default:
    }
  }

  dispatchResult = (data) => {
    switch (data.type) {
      case "reset":
      case "setBusy":
      case "setReady":
        return this.enableDialOut(data)
      case "makeCall":
      case "setAway":
        return this.disableDialOut(data)
      default:
    }
  }

  enableDialOut(data) {
    if (data.code === "000" && this.onUpdateStatusCallback) {
      this.onUpdateStatusCallback(true, data)
    }
  }

  disableDialOut(data) {
    if (data.code === "000" && this.onUpdateStatusCallback) {
      this.onUpdateStatusCallback(false, data)
    }
  }

  onLogin(data) {
    if (this.loginPromise) {
      this.loginPromise.resolve(data)
      this.loginPromise = undefined
    }
    if (this.onUpdateStatusCallback) {
      this.onUpdateStatusCallback(true, data)
    }
  }

  onLoginFail(data) {
    if (this.loginPromise) {
      this.loginPromise.reject(data)
      this.loginPromise = undefined
    }
  }

  onLogout(data) {
    if (this.logoutPromise) {
      this.logoutPromise.resolve(data)
      this.logoutPromise = undefined
    }
    if (this.onUpdateStatusCallback) {
      this.onUpdateStatusCallback(false, data)
    }
  }

  onLogoutFail(data) {
    if (this.logoutPromise) {
      this.logoutPromise.reject(data)
      this.logoutPromise = undefined
    }
  }

  onBusy(data) {
    if (this.busyPromise) {
      this.busyPromise.resolve(data)
      this.busyPromise = undefined
    }
  }

  onReady(data) {
    if (this.readyPromise) {
      this.readyPromise.resolve(data)
      this.readyPromise = undefined
    }
  }

  mergeData2DialStatus(data, callingStatus) {
    const callType = data.ext.callType
    this.dialStatus.sessionId = data.ext["sessionId"]
    this.dialStatus.callingStatus = callingStatus
    this.dialStatus.dialDirection = ChannelSoftDialDirectionMap[callType]
  }

  onOutboundAlertingTp(data) {
    if (data && data.ext) {
      if (data.ext["sessionId"]) {
        if (data.ext["strDnis"] === this.dialStatus.number) {
          if (this.onDailStatusUpdateCallback) {
            this.mergeData2DialStatus(data, "CALLING_STATUS_OUT_AGENT_ALTER")
            this.onDailStatusUpdateCallback(this.dialStatus)
          }
        } else {
          this.dialStatus = {}
        }
      }
    }
  }

  onOutboundConnectedOp(data) {
    // 外呼用户接通时，更新行动记录状态为用户接通
    if (data && data.ext && data.ext["sessionId"]) {
      if (this.onDailStatusUpdateCallback) {
        if (data.ext["strDnis"] === this.dialStatus.number) {
          this.mergeData2DialStatus(data, "CALLING_STATUS_OUT_CUSTOMER_CONNECT")
          this.onDailStatusUpdateCallback(this.dialStatus)
        } else {
          this.dialStatus = {}
        }
      }
    }
  }

  onTpDisconnect(data) {
    this.onDisconnect(data, "CALLING_STATUS_AGENT_DISCONNECT")
  }

  onOpDisconnect(data) {
    this.onDisconnect(data, "CALLING_STATUS_CUSTOMER_DISCONNECT")
  }

  onDisconnect(data, status) {
    if (this.disconnectPromise) {
      this.disconnectPromise.resolve(data)
      this.disconnectPromise = undefined
    }
    if (data && data.ext) {
      if (data.ext["sessionId"]) {
        if (data.ext["strDnis"] === this.dialStatus.number) {
          if (this.onDailStatusUpdateCallback) {
            this.mergeData2DialStatus(data, status)
            this.onDailStatusUpdateCallback(this.dialStatus)
          }
        } else {
          this.dialStatus = {}
        }
      }
    }
    if (this.onUpdateStatusCallback) {
      this.onUpdateStatusCallback(true, data)
    }
  }

  parseTelephone(num) {
    const phone = parsePhone(num)
    // 如果是座机且有分机 调整格式为'[城市号][座机号]-[分机号]'
    if (phone.error) {
      message.error("号码非法: " + phone.error)
    }

    if (phone.telephone) {
      if (!phone.countryCode || phone.countryCode === "86") {
        num = ""
        if (phone.cityCode) {
          num += phone.cityCode
        }
        if (phone.telephone) {
          num += phone.telephone
        }
      } else {
        message.error("固话号码设置错误:" + "禁止设置国际固话号码")
      }
    }
    return num
  }

  parseMobile(num) {
    const phone = parsePhone(num)
    if (phone.error) {
      message.error("号码非法:" + phone.error)
    }

    if (phone.mobile) {
      if (!phone.countryCode || phone.countryCode === "86") {
        num = ""
        if (phone.mobile) {
          num += phone.mobile
        }
      } else {
        message.error("手机号码设置错误:" + "禁止设置国际手机号码")
      }
    }
    return num
  }

  parseCustomerNumber = (number) => {
    const phone = parsePhone(number)
    if (phone.error) {
      message.error("号码非法: " + phone.error)
    }

    if (!phone.countryCode || phone.countryCode === "86") {
      if (phone.telephone) {
        number = ""
        if (phone.cityCode) {
          number += phone.cityCode
        }
        if (phone.telephone) {
          number += phone.telephone
        }
        if (phone.extension && this.isCallTelWithExt) {
          number += "-" + phone.extension
        }
      } else if (phone.mobile) {
        number = ""
        if (phone.mobile) {
          number += phone.mobile
        }
      }
    } else {
      message.error("拨号禁止:" + "禁止拨打国际长途")
    }

    return number
  }
}

export default ChannelSoft
